import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Copy, Trash2, Plus, Loader2 } from 'lucide-react';
import type { GuildInviteResponse } from '@/lib/api';

interface GuildInviteManagerProps {
  invites: GuildInviteResponse[];
  loading: boolean;
  onCreate: (options?: {
    maxUses?: number;
    expiresInHours?: number;
  }) => Promise<GuildInviteResponse>;
  onRevoke: (inviteId: string) => Promise<void>;
}

export function GuildInviteManager({
  invites,
  loading,
  onCreate,
  onRevoke,
}: GuildInviteManagerProps) {
  const [maxUses, setMaxUses] = useState('');
  const [expiresInHours, setExpiresInHours] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      await onCreate({
        maxUses: maxUses ? parseInt(maxUses, 10) : undefined,
        expiresInHours: expiresInHours ? parseInt(expiresInHours, 10) : undefined,
      });
      setMaxUses('');
      setExpiresInHours('');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (code: string) => {
    const encodedCode = encodeURIComponent(code);
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/a/arche_guilds/invite/${encodedCode}`,
      );
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Fallback for browsers without clipboard API access
      const url = `${window.location.origin}/a/arche_guilds/invite/${encodedCode}`;
      window.prompt('Copy this invite link:', url);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Max Uses</Label>
          <Input
            type="number"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            placeholder="Unlimited"
            min={1}
            className="w-28"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Expires (hours)</Label>
          <Input
            type="number"
            value={expiresInHours}
            onChange={(e) => setExpiresInHours(e.target.value)}
            placeholder="Never"
            min={1}
            max={720}
            className="w-28"
          />
        </div>
        <Button onClick={handleCreate} disabled={creating}>
          {creating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          Create Invite
        </Button>
      </div>
      {createError && <p className="text-sm text-destructive">{createError}</p>}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading invites...</p>
      ) : invites.length === 0 ? (
        <p className="text-sm text-muted-foreground">No active invites.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Uses</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="w-[100px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map((inv) => (
              <TableRow key={inv.inviteId}>
                <TableCell className="font-mono text-sm">{inv.inviteCode}</TableCell>
                <TableCell className="text-sm">
                  {inv.useCount}
                  {inv.maxUses != null ? ` / ${inv.maxUses}` : ' / \u221e'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {inv.expiresAt ? new Date(inv.expiresAt).toLocaleString() : 'Never'}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyLink(inv.inviteCode)}
                      aria-label="Copy invite link"
                    >
                      <Copy className="h-4 w-4" />
                      {copied === inv.inviteCode && <span className="ml-1 text-xs">Copied!</span>}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to revoke this invite?')) {
                          onRevoke(inv.inviteId);
                        }
                      }}
                      aria-label="Revoke invite"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
